import classNames from 'classnames';
import isArray from 'lodash/isArray';
import isString from 'lodash/isString';
import PropTypes from 'prop-types';
import { stringify as stringifyQuery } from 'query-string';
import React, { Fragment, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';
import { capitalizeFirstLetter } from '../../lib/utils';

import { useIndexUrlGenerator } from '../../contexts/RoutesContext';
import Image from './Image';
import Link from './Link';

import styles from '../../../styles/partials/film-metadata.module.scss';

const propTypes = {
    item: AppPropTypes.film.isRequired,
    className: PropTypes.string,
    isDark: PropTypes.bool,
};

const defaultProps = {
    className: null,
    isDark: false,
};

const FilmMetadata = ({ item, className, isDark }) => {
    const {
        titleOriginal = null,
        colour = null,
        year = null,
        countries = [],
        province = null,
        languages = [],
        subtitleLanguages = [],
        directors = [],
        producers = [],
        writers = [],
        cast = [],
        links = [],
        lengthType = null,
        poster = null,
        edition = null,
    } = item || {};
    const { handle: editionHandle } = edition || {};

    // console.log(item);

    const route = useIndexUrlGenerator('films');

    const metadata = useMemo(
        () =>
            [
                {
                    label: (
                        <FormattedMessage
                            defaultMessage="Original title"
                            description="Metadata label"
                        />
                    ),
                    value: titleOriginal,
                },
                {
                    label: <FormattedMessage defaultMessage="Year" description="Metadata label" />,
                    value: year,
                },
                {
                    label: (
                        <FormattedMessage
                            defaultMessage="{count, plural, one {Country} other {Countries}}"
                            description="Metadata label"
                            values={{
                                count: countries.length,
                            }}
                        />
                    ),
                    value: countries,
                    query: 'country',
                },
                {
                    label: (
                        <FormattedMessage defaultMessage="Province" description="Metadata label" />
                    ),
                    value: province,
                },
                {
                    label: (
                        <FormattedMessage
                            defaultMessage="{count, plural, one {Director} other {Directors}}"
                            description="Metadata label"
                            values={{
                                count: directors.length,
                            }}
                        />
                    ),
                    value: directors.map(({ id, name: label }) => ({
                        id,
                        label,
                    })),
                    query: 'director',
                },
                {
                    label: (
                        <FormattedMessage
                            defaultMessage="Production"
                            description="Metadata label"
                            values={{
                                count: producers.length,
                            }}
                        />
                    ),
                    value: producers.map(({ id, name: label }) => ({
                        id,
                        label,
                    })),
                    query: 'producer',
                },
                {
                    label: (
                        <FormattedMessage
                            defaultMessage="Type of work"
                            description="Metadata label"
                        />
                    ),
                    value: lengthType,
                    query: 'length_type',
                },
                {
                    label: (
                        <FormattedMessage defaultMessage="Colour" description="Metadata label" />
                    ),
                    value: colour,
                    query: 'colour',
                },
                {
                    label: (
                        <FormattedMessage
                            defaultMessage="{count, plural, one {Writer} other {Writers}}"
                            description="Metadata label"
                            values={{
                                count: writers.length,
                            }}
                        />
                    ),
                    value: writers.map(({ id, name: label }) => ({
                        id,
                        label,
                    })),
                    query: 'writer',
                },
                {
                    label: <FormattedMessage defaultMessage="Cast" description="Metadata label" />,
                    value: cast.slice(0, 3).map(({ id, name: label }) => ({
                        id,
                        label,
                    })),
                    query: 'cast',
                },
                {
                    label: (
                        <FormattedMessage
                            defaultMessage="{count, plural, one {Language} other {Languages}}"
                            description="Metadata label"
                            values={{
                                count: languages.length,
                            }}
                        />
                    ),
                    value: languages,
                    query: 'language',
                },
                {
                    label: (
                        <FormattedMessage
                            defaultMessage="{count, plural, one {Subtitle language} other {Subtitle languages}}"
                            description="Metadata label"
                            values={{
                                count: subtitleLanguages.length,
                            }}
                        />
                    ),
                    value: subtitleLanguages,
                    query: 'subtitle',
                },
                {
                    label: (
                        <FormattedMessage
                            defaultMessage="{count, plural, one {Link} other {Links}}"
                            description="Metadata label"
                            values={{
                                count: links.length,
                            }}
                        />
                    ),
                    value: links,
                },
            ]
                .map(({ label, value, query = null }, index) => {
                    const multiple = isArray(value);
                    return value !== null && (!multiple || value.length > 0)
                        ? {
                              id: `metadata-${index + 1}`,
                              label,
                              values: (multiple ? value : [value])
                                  .filter((it) => it !== null)
                                  .map((it) => {
                                      const {
                                          id,
                                          label: valueLabel,
                                          url = null,
                                      } = isString(it) ? { label: it } : it;
                                      return {
                                          label: valueLabel,
                                          url:
                                              url ||
                                              (query !== null
                                                  ? `${route('index_edition', {
                                                        edition: editionHandle,
                                                    })}?${stringifyQuery(
                                                        {
                                                            [query]: multiple ? [id] : id,
                                                        },
                                                        { arrayFormat: 'bracket' },
                                                    )}`
                                                  : null),
                                          external: url !== null,
                                          query,
                                      };
                                  }),
                          }
                        : null;
                })
                .filter((it) => it !== null),
        [directors, colour, lengthType, countries, languages, subtitleLanguages],
    );

    return (
        <dl
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [styles.isDark]: isDark,
                },
            ])}
        >
            {poster !== null && (
                <Image
                    className={styles.poster}
                    imageClassName={styles.posterImage}
                    media={poster}
                    mediaSize="medium"
                />
            )}
            <div className={styles.items}>
                {metadata.map(({ id, label, values }) => (
                    <div key={id} className={styles.item}>
                        <dt>{label}</dt>
                        <dd>
                            {values.map(
                                (
                                    { label: valueLabel, url, external = false, query = null },
                                    index,
                                ) => (
                                    <Fragment key={`${id}-${index + 1}`}>
                                        {url !== null ? (
                                            <Link href={url} external={external}>
                                                {query === 'language' && index === 0
                                                    ? capitalizeFirstLetter(valueLabel)
                                                    : valueLabel}
                                            </Link>
                                        ) : (
                                            valueLabel
                                        )}
                                        {values.length > 0 && index !== values.length - 1 && ', '}
                                    </Fragment>
                                ),
                            )}
                        </dd>
                    </div>
                ))}
            </div>
        </dl>
    );
};
FilmMetadata.propTypes = propTypes;
FilmMetadata.defaultProps = defaultProps;

export default FilmMetadata;
